import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getMe } from '@/api';
import { getUser, isLoggedIn, clearToken, getToken, setToken } from '@/auth/token';
import ThemeToggle from '@/theme/ThemeToggle';
import type { HostedCreditsMode } from '../hostedCredits';

type Props = {
  pipelineName: string;
  setPipelineName: (v: string) => void;
  version: string;
  status: 'ready' | 'saved' | 'unsaved';
  running: boolean;
  saving?: boolean;
  runLabel?: string;
  locked?: boolean;
  hostedCredits?: HostedCreditsMode;
  onRun: () => void;
  onExport: () => void;
  onFork: () => void;
  onSave?: () => void;
};

const statusDot: Record<Props['status'], string> = {
  ready: 'bg-success',
  saved: 'bg-warning',
  unsaved: 'bg-error',
};

export default function Header({ pipelineName, setPipelineName, version, status, running, saving, runLabel, locked, hostedCredits, onRun, onExport, onFork, onSave }: Props) {
  const [editing, setEditing] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [user, setUser] = useState(getUser());
  const logged = isLoggedIn();
  const loc = useLocation();
  const loginHref = `/login?next=${encodeURIComponent(loc.pathname + loc.search)}`;

  useEffect(() => {
    if (!isLoggedIn()) return;
    getMe()
      .then((u) => {
        const tok = getToken();
        if (tok) setToken(tok, u);
        setUser(u);
      })
      .catch(() => { /* ignore */ });
  }, []);

  return (
    <header data-testid="studio-header" className="h-16 flex items-center justify-between px-6 border-b border-border bg-bg z-20 relative">
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Link to="/" className="w-7 h-7 rounded-md bg-accentSoft border border-accentLine flex items-center justify-center hover:bg-accent/30 transition-colors">
            <i className="ri-stack-line text-accent text-base" />
          </Link>
          <Link to="/studio" className="text-textp font-semibold tracking-tight">Sourcetape</Link>
        </div>
        <div className="h-6 w-px bg-border" />
        <div className="flex items-center gap-2">
          {editing ? (
            <input
              autoFocus
              value={pipelineName}
              onChange={(e) => setPipelineName(e.target.value)}
              onBlur={() => setEditing(false)}
              onKeyDown={(e) => e.key === 'Enter' && setEditing(false)}
              className="bg-surface border border-border rounded-md px-2 py-1 text-sm text-textp outline-none focus:border-accent"
            />
          ) : (
            <button onClick={() => { if (!locked) setEditing(true); }} className="text-sm text-textp hover:text-textp/80 flex items-center gap-1.5 whitespace-nowrap">
              <i className="ri-git-repository-line text-texts" />
              {pipelineName}
              <i className="ri-edit-line text-texts text-xs opacity-0 hover:opacity-100" />
            </button>
          )}
          <span className="text-xs font-mono px-1.5 py-0.5 rounded bg-surface border border-border text-texts">{version}</span>
          <span className={`w-2 h-2 rounded-full ${statusDot[status]}`} title={status} />
        </div>
      </div>

      <div className="flex items-center gap-2">
        {hostedCredits && (
          <Link
            to={hostedCredits === 'login' ? loginHref : '/dashboard?view=credits'}
            data-testid="hosted-credits-header"
            className="whitespace-nowrap flex items-center gap-1.5 text-accent hover:text-accent-hover text-sm px-3 py-1.5 rounded-md"
          >
            <i className="ri-coin-line" /> {hostedCredits === 'login' ? 'Sign in for hosted' : 'Start hosting'}
          </Link>
        )}
        {onSave && (
          <button onClick={onSave} disabled={saving || locked} className="whitespace-nowrap flex items-center gap-1.5 bg-surface hover:bg-surface2 border border-border text-textp text-sm px-3 py-1.5 rounded-md disabled:opacity-60">
            {saving ? <><i className="ri-loader-4-line animate-spin" /> Saving</> : <><i className="ri-save-line" /> Save</>}
          </button>
        )}
        <button data-testid="run-pipeline" onClick={onRun} disabled={running} className="whitespace-nowrap flex items-center gap-1.5 bg-accent hover:bg-accent-hover disabled:opacity-60 text-accent-text text-sm font-medium px-3.5 py-1.5 rounded-md">
          {running ? (
            <><i className="ri-loader-4-line animate-spin" /> {runLabel || 'Running...'}</>
          ) : (
            <><i className="ri-play-fill" /> Run</>
          )}
        </button>
        <button onClick={onExport} disabled={locked} className="whitespace-nowrap flex items-center gap-1.5 bg-surface hover:bg-surface2 border border-border text-textp text-sm px-3 py-1.5 rounded-md disabled:opacity-60">
          <i className="ri-download-line" /> Export
        </button>
        <button data-testid="studio-fork" onClick={onFork} disabled={locked} className="whitespace-nowrap flex items-center gap-1.5 text-texts hover:text-textp text-sm px-3 py-1.5 rounded-md disabled:opacity-60">
          <i className="ri-git-branch-line" /> Fork
        </button>
        {user?.role === 'admin' && (
          <Link to="/admin" data-testid="superadmin-link" className="whitespace-nowrap flex items-center gap-1.5 text-texts hover:text-textp text-sm px-3 py-1.5 rounded-md">
            <i className="ri-shield-user-line" /> Superadmin
          </Link>
        )}
        <ThemeToggle className="px-3 py-1.5 rounded-md" showLabel />
        <div className="relative">
          <button onClick={() => setMenuOpen((o) => !o)} className="whitespace-nowrap flex items-center gap-1.5 text-texts hover:text-textp text-sm px-3 py-1.5 rounded-md">
            <i className="ri-user-line" /> {logged ? (user?.name || user?.email || 'Account') : 'Sign in'}
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-full mt-1 w-44 bg-surface border border-border rounded-md py-1 z-30" onClick={() => setMenuOpen(false)}>
              {logged ? (
                <>
                  <Link to="/" className="block px-3 py-1.5 text-xs text-textp hover:bg-surface2">Home</Link>
                  <Link to="/dashboard" className="block px-3 py-1.5 text-xs text-textp hover:bg-surface2">Dashboard</Link>
                  {user?.role === 'admin' && (
                    <Link to="/admin" data-testid="superadmin-menu-link" className="block px-3 py-1.5 text-xs text-textp hover:bg-surface2">Superadmin</Link>
                  )}
                  <button onClick={() => { clearToken(); location.assign('/'); }} className="w-full text-left block px-3 py-1.5 text-xs text-textp hover:bg-surface2">Sign out</button>
                </>
              ) : (
                <>
                  <Link to="/login" className="block px-3 py-1.5 text-xs text-textp hover:bg-surface2">Login</Link>
                  <Link to="/register" className="block px-3 py-1.5 text-xs text-textp hover:bg-surface2">Register</Link>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
